import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import { resetFilterState, setFilterState } from '../../slicers/FilterSlice';
import managementStyles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice';


const PatientLocation = () => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);

    const user = useSelector((state) => state.userInfo);
    const [patientLocation, setPatientLocation] = useState([]);

    const headers = [
        { key: 'id', label: 'Perin ID' },
        { key: 'encounterId', label: "Encounter ID" },
        { key: 'mrn', label: "MRN" },
        { key: 'fullName', label: "Name" },
        { key: 'location', label: "Location" },
        { key: 'floor', label: 'Floor' },
        { key: 'room', label: 'Room' },
        { key: 'bed', label: 'Bed' },
        { key: 'perinPatchSerialNumber', label: 'Patch Serial Number' },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
    ];

    useEffect(() => {
        getPatientLocation();
        getPatientLocationHierarchy();
    }, []);

    const getPatientLocationHierarchy = async () => {
        try {
            const responseData = await axios.get(`patient/locations/hierarchy/${user.id}`);
            if (Array.isArray(responseData.data.data)) {
                dispatch(setFilterState({'key': 'inpatientLocationHierarchy', 'value': responseData.data.data}));
            }
        } catch (err) {
            console.error('Error fetching patient location hierarchy:', err);
        }
    };

    const getPatientLocation = async () => {
        dispatch(resetFilterState());
        try {
            const responseData = await axios.get(`patient/ehr/list/${user.id}`);
            if (Array.isArray(responseData.data.data)) {
                const patientIds = responseData.data.data.map(item => item.id);
                // Fetch enrollments for all patients
                const enrollmentsPromises = patientIds.map(async (patientId) => {
                    try {
                        const response = await axios.get(`enrollment/patient/active/${patientId}`);
                        if ((response.data?.success || response.data?.code === 200) && response.data?.data) {
                            const enrollment = response.data.data;
                            return { patientId, enrollment: enrollment };
                        }
                        return { patientId, enrollment: null };
                    } catch (err) {
                        return { patientId, enrollment: null };
                    }
                });
                
                const enrollmentsArray = await Promise.all(enrollmentsPromises);
                
                // Create a map of patientId to enrollment
                const enrollmentsMap = enrollmentsArray.reduce((acc, { patientId, enrollment }) => {
                    acc[patientId] = enrollment;
                    return acc;
                }, {});
                
                // Helper function to get patch serial number from enrollment
                const getPatchSerialNumber = (enrollment) => {
                    if (!enrollment || !enrollment[0] || !enrollment[0].devices) {
                        return null;
                    }
                    const patchDevice = enrollment[0].devices.find(device => device.deviceId === 1);
                    return patchDevice?.serialNumber || null;
                };
                
                const formattedData = responseData.data.data.map(item => {
                    const enrollment = enrollmentsMap[item.id] ?? null;
                    const perinPatchId = getPatchSerialNumber(enrollment);
                    
                    return {
                        fullName: `${item.firstName} ${item.lastName}`,
                        encounterId: item.patientLocation.encounterId,
                        mrn: item.patientLocation.mrn,
                        location: item.patientLocation.location,
                        floor: item.patientLocation.floor,
                        room: item.patientLocation.room,
                        bed: item.patientLocation.bed,
                        perinPatchSerialNumber: perinPatchId || '',
                        phone: item.patientLocation.phone,
                        email: item.patientLocation.email,
                        perinUserId: item.patientLocation.perinUserId,
                        organizationId: item.patientLocation.organizationId,
                        ...item
                    };
                });
                setPatientLocation(formattedData);
            } else {
                console.error('Invalid data format:');
            }
            
        } catch (err) {
            console.error('Error fetching patient locations:', err);
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Failed to fetch patient locations",
                    type: 'danger',
                })
            );
        }
    };

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={true}
                isGroup={false}
                isSubGroup={false}
                isTeam={false}
                showAddButton={false}
                showDeleteButton={false}
                showInpatientFilters={true}

            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={patientLocation}
                    headers={headers}
                    showCheckbox={true}
                    showEditIcon={false}
                    showHeaderFilter={true}
                    gridHeight="100%"
                    inpatientFilters={true}
                />
            </div>
        </div>
    );
};

export default PatientLocation;
